import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Skeleton, Container, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { RouteProps, useLocation } from 'react-router-dom';

import { useVerticalState } from 'common/hooks';
import { SettingsMenu } from 'contractor/modules/Settings';
import { SettingsProvider, useSettings } from 'contractor/modules/Settings/useSettings';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 32,
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      justifyContent: 'space-around',
    },
  },
  menu: {
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      top: 32,
    },
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 32,
    alignItems: 'flex-start',
  },
}));

type Props = {
  children?: React.ReactNode;
} & RouteProps;

const SettingsContainer = ({ children }: Props): JSX.Element => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { contractor, organization, query } = useSettings();
  const [{ brandMetadata }] = useVerticalState();
  const pageTitle = `Settings | ${brandMetadata.label}`;

  // using approach found here: https://www.carlrippon.com/scrolling-a-react-element-into-view/
  // as react-scroll does not appear to work as expected on mount
  const scrollElementRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [pathname]);

  if (!query.data || !contractor || !organization) {
    return (
      <Container maxWidth="md" className={classes.skeleton}>
        <Skeleton variant="rectangular" width={180} height={216} />
        <Box mr={3} />
        <Skeleton variant="rectangular" width={500} height={600} />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Container maxWidth="md">
        <Box className={classes.root} mb={8}>
          <Box className={classes.menu}>
            <SettingsMenu />
          </Box>
          <Box width="100%">
            <div ref={scrollElementRef} />
            {children}
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default ((props: Props): JSX.Element => (
  <SettingsProvider>
    <SettingsContainer {...props} />
  </SettingsProvider>
));
